import React, { useEffect, useMemo, useState } from 'react'
import { Alert, Autocomplete, Button, FormControl, Snackbar, TextField } from '@mui/material'
import { useTranslation } from 'react-i18next'
import TPCheckbox from "../../TemplateProperties/TPCheckbox";
import DelimiterDialog from '../../TemplateProperties/DelimiterDialog';
import _, { debounce } from 'lodash';
import { stringifyJSONAttributes, stringToBooleanConversion } from '../../../utils/utilFunctions';
import { useDispatch } from 'react-redux';
import { api } from '../../../redux/api';
import { MoreHorizOutlined } from '@mui/icons-material';
import WorkflowSessionModal from '../../ItemProperties/WorkflowSessionModal';

const PRIVATE_KEY_ALIAS = [];

const Socket = ({ connectionDetails, handleBodyContent, direction, account, screen, disableControl, repoPDC, useConnection }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [ipAddress, setIpAddress] = useState('');
  const [port, setPort] = useState('');
  const [dataTerminator, setDataTerminator] = useState(null);
  const [dialogState, setDialogState] = useState(false);
  const [outTime, setOutTime] = useState(0);
  const [socketAlive, setSocketAlive] = useState(false);
  // const [decompression, setDecompression] = useState(false);
  const [synchronous, setSynchronous] = useState(false);
  const [compression, setCompression] = useState(false);
  const [ssl, setSsl] = useState(false);
  const [privateKeys, setPrivateKeys] = useState(PRIVATE_KEY_ALIAS);
  const [privateKey, setPrivateKey] = useState('');
  const [content, setContent] = useState(null);
  const [workflowSessionModal, setWorkflowSessionModal] = useState({open: false, isFirstTime: false});
  const [valueLabel, setValueLabel] = useState("");
  
  const [showAlert, setShowAlert] = useState({ mssg: '', type: 'success', isOpen: false });
  const workflowMappings = {
    [t("LBL_IP_ADDR")]: {
      stateSetter: setIpAddress,
      key: 'CONNECTIONSTRING',
      value: ipAddress,
    },
    [t("LBL_PORT")]: {
      stateSetter: setPort,
      key: 'PORT',
      value: port,
    },
    [t("LBL_TIMEOUT")]: {
      stateSetter: setOutTime,
      key: 'TIMEOUT',
      value: outTime,
    },
    [t("KEY_ALIAS")]: {
      stateSetter: setPrivateKey,
      key: 'KEY_ALIAS',
      value: privateKey,
    }
  };
   const valuesHandler = (value) => {
     if (workflowMappings[valueLabel]) {
       const { stateSetter, key } = workflowMappings[valueLabel];

       stateSetter(value);
       onInputChange(key, value);
     }
   };

  const handleAlertClose = () => {
    setShowAlert({ ...showAlert, isOpen: false });
  }

  const handleDataTerminator = (val) => {
    if (val) {
      setDataTerminator(delimDisplay(val, true));
      onInputChange('EOD_TERMINATOR', (val));
    } else {
      onInputChange('EOD_TERMINATOR', (val));
      setDataTerminator(val)
    }
  }

  const delimDisplay = (val, modal) => {

    // Display values in certain ways depending on a few specific conditions:
    // - If no delim val -> 'No Delimiter Chosen'.
    // - Replace 'CR' and 'LF' (ascii 13 and 10) OR '%0D' '%0A' -> display 'CR', 'LF', 'CRLF' in latin characters.
    // - If val is one or two char length -> display 'ASCII 42 (*)' delimited by comma.
    // - else -> display the decoded string (even if it's just empty chars ?)
    if (val === undefined) return '';
    if (!modal) {
      if (typeof val === 'boolean' || val === t('TXT_NO_DELIM_CHOSEN') || !val) return t('TXT_NO_DELIM_CHOSEN');
    }
    if (val === "CR" || val === "%0D" || val === "\r") return "CR";
    if (val === "LF" || val === "%0A" || val === "\n") return "LF";
    if (val === "CRLF" || val === "%0D%0A" || val === '\r\n' ) return "CRLF";

    try { val = decodeURIComponent(val) }
    catch (e) {
      //case - malformed URI
    }
    return val.length < 3 ?
      val.split('').map(e => `ASCII ${e.charCodeAt(0)} (${e})`).join(', ')
      : val;
  }

  const debouncedContentUpdate = debounce(handleBodyContent, 500);

  const onInputChange = (name, value) => {
    if(screen && (screen === 'GET_DATA' || screen === 'SEND_DATA')){
      if (name === "EOD_TERMINATOR") {
        value = decodeURIComponent(value)
        if (value === "" || value === undefined || value === null)
          setContent({ remove: { EOD_TERMINATOR: true } });
        else setContent({ [name]: (value).replace(/~/g, '%7E') });
        return;
      } else if (name === "CONNECTIONSTRING") {
        setContent({ [name]: (value) });
        return;
      }
      setContent({[name]: value})
    } else {
      setContent((prev) => {
        return { ...prev, [name]: value };
      });
    }
   
  }
  
  const getFontWeight = (
    control,
    currentValue,
    useConnection
  ) => {
    if (control === "TIMEOUT" && currentValue === "0") return "normal";
    return repoPDC?.[control] !== currentValue && useConnection === "preDefined"
      ? "bold"
      : "normal";
  };
  
  const fontWeights = useMemo(() => {
    return {
      TIMEOUT: getFontWeight(
        "TIMEOUT",
        outTime,
        useConnection
      ),
      PORT: getFontWeight(
        "PORT", 
        port, 
        useConnection
      ),
      PRIVATE_KEY: getFontWeight(
        "KEY_ALIAS",
        privateKey,
        useConnection
      ),
      CONNECTIONSTRING: getFontWeight(
        "CONNECTIONSTRING",
        ipAddress,
        useConnection
      ),
      EOD_TERMINATOR: getFontWeight(
        "EOD_TERMINATOR",
        dataTerminator,
        useConnection
      ),
    };
  }, [
    outTime,
    port,
    dataTerminator,
    ipAddress,
    useConnection,
    privateKey,
  ]);
  useEffect(() => {
    if (direction === 'IN') {
      // setIpAddress(connectionDetails?.CONNECTIONSTRING || `http://[SERVER_URL]/services/${account}/`);
      setIpAddress(connectionDetails?.CONNECTIONSTRING ? 
        (connectionDetails?.CONNECTIONSTRING): '');
      setPort(connectionDetails?.PORT || '');

      // setPort(connectionDetails?.PORT || 143);
    }
    if (direction === 'OUT') {
      setIpAddress(connectionDetails?.CONNECTIONSTRING || ``);
      setPort(connectionDetails?.PORT || '');
    }
    setPrivateKey(connectionDetails?.KEY_ALIAS || '');
    setDataTerminator(connectionDetails?.EOD_TERMINATOR ? delimDisplay((connectionDetails?.EOD_TERMINATOR)) : "");
    setOutTime(connectionDetails?.TIMEOUT || 0);
    setSocketAlive(stringToBooleanConversion(connectionDetails?.KEEP_ALIVE));
    setSynchronous(stringToBooleanConversion(connectionDetails?.SYNCHRONOUS));
    // setDecompression(stringToBooleanConversion(connectionDetails?.ZIP_DECOMPRESS));
    // setCompression(stringToBooleanConversion(connectionDetails?.COMPRESSION));
    setSsl(stringToBooleanConversion(connectionDetails?.SSL));
    setContent({ ...connectionDetails })
  }, [connectionDetails]);

  useEffect(() => {
    if(!_.isEqual(connectionDetails, content)) debouncedContentUpdate(content);
  }, [content]);

  useEffect(() => {
    if (direction === 'OUT' && ssl)
      dispatch(api.endpoints.getPrivateKeys.initiate()).then((res) => {
        if (res?.data?.current_state === 'SUCCESS') {
          setPrivateKeys(res?.data?.data);
        } 
        if (res?.data?.current_state === 'ERROR') {
          setShowAlert({ mssg: res?.data?.message, isOpen: true, type: 'error' });
        }
      })
  }, [ssl])

  return (
    <>
      <div style={{ display: "flex", gap: "4px" }}>
        <FormControl className="width-100" size="small">
          <TextField
            variant="outlined"
            size="small"
            disabled={disableControl || false}
            label={t("LBL_IP_ADDR")}
            type="text"
            value={ipAddress}
            onChange={(e) => {
              onInputChange(e.target.name, e.target.value);
              setIpAddress(e.target.value);
            }}
            className={"width-100"}
            sx={{ input: { fontWeight: fontWeights.CONNECTIONSTRING } }}
            name={"CONNECTIONSTRING"}
            autoComplete="off"
          />
        </FormControl>
        {screen && (screen === "GET_DATA" || screen === "SEND_DATA") && (
          <Button
            size="small"
            disabled={disableControl || false}
            variant="outlined"
            onClick={() => {
              setValueLabel(t("LBL_IP_ADDR"));
              setWorkflowSessionModal({ open: true, isFirstTime: true });
            }}
            sx={{ width: "40px", minWidth: "40px" }}>
            <MoreHorizOutlined fontSize="small" />
          </Button>
        )}
      </div>
      <div style={{ display: "flex", gap: "4px" }}>
        <FormControl className="width-100" size="small">
          <TextField
            variant="outlined"
            size="small"
            disabled={disableControl || false}
            label={t("LBL_PORT")}
            type="text"
            value={port}
            onChange={(e) => {
              onInputChange(e.target.name, e.target.value);
              setPort(e.target.value);
            }}
            className={"width-100"}
            sx={{ input: { fontWeight: fontWeights.PORT } }}
            name={"PORT"}
            autoComplete="off"
          />
        </FormControl>
        {screen && (screen === "GET_DATA" || screen === "SEND_DATA") && (
          <Button
            size="small"
            disabled={disableControl || false}
            variant="outlined"
            onClick={() => {
              setValueLabel(t("LBL_PORT"));
              setWorkflowSessionModal({ open: true, isFirstTime: true });
            }}
            sx={{ width: "40px", minWidth: "40px" }}>
            <MoreHorizOutlined fontSize="small" />
          </Button>
        )}
      </div>
      {ssl && (
        <div style={{ display: "flex", gap: "4px" }}>
          <Autocomplete
            disablePortal
            disabled={disableControl || false} 
            options={privateKeys}
            value={privateKey}
            sx={{ maxHeight: 300, width: "100%" }}
            onChange={(e, value) => {
              onInputChange("KEY_ALIAS", value);
              setPrivateKey(value);
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label={t("KEY_ALIAS")}
                value={privateKey}
                onChange={(event) => {
                  onInputChange("KEY_ALIAS", event.target.value);
                  setPrivateKey(event.target.value);
                }}
                sx={{ input: { fontWeight: fontWeights.PRIVATE_KEY } }}
                variant="outlined"
                size="small"
                name={"KEY_ALIAS"}
                autoComplete="off"
              />
            )}
          />
          {screen && (screen === "GET_DATA" || screen === "SEND_DATA") && (
            <Button
              size="small"
              disabled={disableControl || false}
              variant="outlined"
              onClick={() => {
                setValueLabel(t("KEY_ALIAS"));
                setWorkflowSessionModal({ open: true, isFirstTime: true });
              }}
              sx={{ width: "40px", minWidth: "40px" }}>
              <MoreHorizOutlined fontSize="small" />
            </Button>
          )}
        </div>
      )}
      {direction === "IN" && (
        <>
          <FormControl className="width-100" size="small">
            <TextField
              size="small"
              variant="outlined"
              className="width-100 delim-button"
              disabled={disableControl || false}
              label={t("LBL_EOD_TERMINATOR")}
              value={dataTerminator || t("TXT_NO_DELIM_CHOSEN")}
              onClick={() => setDialogState(true)}
              InputProps={{
                readOnly: true,
                sx: { textAlign: "center", fontWeight: fontWeights.EOD_TERMINATOR },
                inputProps: { style: { textAlign: "center" } },
              }}
            />
          </FormControl>
          <DelimiterDialog
            modalOpen={dialogState}
            close={() => setDialogState(false)}
            handleInput={handleDataTerminator}
            val={dataTerminator || ""}
            name={"EOD_TERMINATOR"}
            from={"EOD_TERMINATOR"}></DelimiterDialog>
          <div style={{ display: "flex", gap: "4px" }}>
            <FormControl className="width-100" size="small">
              <TextField
                variant="outlined"
                size="small"
                disabled={disableControl || false}
                label={t("LBL_TIMEOUT")}
                type="number"
                value={outTime}
                onChange={(e) => {
                  const inputValue = e.target.value;
                  const numericValue = inputValue.replace(/\D/g, "");
                  if (numericValue.length <= 9) {
                    setOutTime(numericValue);
                    onInputChange(e.target.name, numericValue);
                  }
                }}
                className={"width-100"}
                sx={{ input: { fontWeight: fontWeights.TIMEOUT } }}
                name={"TIMEOUT"}
                autoComplete="off"
              />
            </FormControl>
            {screen && (screen === "GET_DATA" || screen === "SEND_DATA") && (
              <Button
                size="small"
                disabled={disableControl || false}
                variant="outlined"
                onClick={() => {
                  setValueLabel(t("LBL_TIMEOUT"));
                  setWorkflowSessionModal({ open: true, isFirstTime: true });
                }}
                sx={{ width: "40px", minWidth: "40px" }}>
                <MoreHorizOutlined fontSize="small" />
              </Button>
            )}
          </div>
        </>
      )}
      <div
        className={`flex flex-wrap ${
          (direction === "OUT" && !screen) ? "flex-column" : ""
        }`}>
        <TPCheckbox
          label={t("BTN_USE_KEEP_ALIVE")}
          disabled={disableControl || false}
          checked={socketAlive}
          handleCheck={(checked) => {
            onInputChange("KEEP_ALIVE", stringifyJSONAttributes(checked));
            setSocketAlive(checked);
          }}
        />
        {direction === "OUT" && (
          <>
            <TPCheckbox
              label={t("SSL")}
              checked={ssl}
              handleCheck={(checked) => {
                onInputChange("SSL", stringifyJSONAttributes(checked));
                setSsl(checked);
              }}
            />
            {screen === "SEND_DATA" && <TPCheckbox
              label={t("BTN_WS_RECEIVE_SYNC_RESPONSE")}
              checked={synchronous}
              handleCheck={(checked) => {
                onInputChange("SYNCHRONOUS", stringifyJSONAttributes(checked));
                setSynchronous(checked);
              }}
            />}
          </>
        )}
      </div>
      <WorkflowSessionModal
        valueLabel={valueLabel}
        showAccountData={true}
        showCallingWorkflow={true}
        showSystemData={true}
        showWorkflowSession={true}
        showDateAndTime={true}
        openModal={workflowSessionModal}
        setModalOpen={setWorkflowSessionModal}
        handleClose={() =>
          setWorkflowSessionModal({ open: false, isFirstTime: false })
        }
        initialValue={workflowMappings[valueLabel]?.value}
        valuesHandler={(value) => {
          valuesHandler(value);
        }}
      />
      <Snackbar open={showAlert.isOpen} onClose={() => handleAlertClose()}>
        <Alert
          onClose={() => handleAlertClose()}
          severity={showAlert.type}
          variant="filled"
          sx={{ width: "100%" }}>
          {showAlert.mssg}
        </Alert>
      </Snackbar>
    </>
  )
}

export default Socket